import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, Search, Image as ImageIcon } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { adminAdsAPI } from '../../utils/api';
import { toast } from '../../hooks/use-toast';
import { Toaster } from '../ui/toaster';

const AdsManagement = () => {
  const [ads, setAds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedAd, setSelectedAd] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    image_path: '',
    description: '',
    status: 'active',
    display_order: 0,
  });

  useEffect(() => {
    loadAds();
  }, [searchTerm]);

  const loadAds = async () => {
    try {
      setLoading(true);
      const params = searchTerm ? { search: searchTerm } : {};
      const response = await adminAdsAPI.getAll(params);
      setAds(response.data);
    } catch (error) {
      toast({
        title: "Error",
        description: error.message || "No se pudieron cargar los anuncios",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (ad = null) => {
    if (ad) {
      setSelectedAd(ad);
      setFormData({
        title: ad.title || '',
        image_path: ad.image_path || '',
        description: ad.description || '',
        status: ad.status || 'active',
        display_order: ad.display_order || 0,
      });
    } else {
      setSelectedAd(null);
      setFormData({
        title: '',
        image_path: '',
        description: '',
        status: 'active',
        display_order: 0,
      });
    }
    setIsModalOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (selectedAd) {
        await adminAdsAPI.update(selectedAd.id, formData);
        toast({ title: "Éxito", description: "Anuncio actualizado exitosamente" });
      } else {
        await adminAdsAPI.create(formData);
        toast({ title: "Éxito", description: "Anuncio creado exitosamente" });
      }
      setIsModalOpen(false);
      loadAds();
    } catch (error) {
      toast({
        title: "Error",
        description: error.message || "Error al guardar el anuncio",
        variant: "destructive",
      });
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Estás seguro de eliminar este anuncio?')) return;
    try {
      await adminAdsAPI.delete(id);
      toast({ title: "Éxito", description: "Anuncio eliminado exitosamente" });
      loadAds();
    } catch (error) {
      toast({
        title: "Error",
        description: error.message || "Error al eliminar el anuncio",
        variant: "destructive",
      });
    }
  };

  return (
    <div className="p-6 space-y-6">
      <Toaster />
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-white">Anuncios</h2>
          <p className="text-[#A2A2A2]">Gestiona los anuncios del outlet</p>
        </div>
        <Button onClick={() => handleOpenModal()} className="bg-white text-black hover:bg-white/90">
          <Plus className="w-4 h-4 mr-2" /> Nuevo anuncio
        </Button>
      </div>

      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-[#A2A2A2] w-5 h-5" />
        <Input
          type="text"
          placeholder="Buscar por título..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-10 bg-white/5 border-white/10 text-white"
        />
      </div>

      {loading ? (
        <div className="text-center py-12 text-[#A2A2A2]">Cargando...</div>
      ) : ads.length === 0 ? (
        <div className="text-center py-12 text-[#A2A2A2]">No hay anuncios registrados</div>
      ) : (
        <div className="bg-white/5 border border-white/10 rounded-2xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[900px]">
            <thead className="bg-white/5">
              <tr>
                <th className="px-6 py-4 text-left text-sm font-medium text-white">Imagen</th>
                <th className="px-6 py-4 text-left text-sm font-medium text-white">Título</th>
                <th className="px-6 py-4 text-left text-sm font-medium text-white">Descripción</th>
                <th className="px-6 py-4 text-left text-sm font-medium text-white">Orden</th>
                <th className="px-6 py-4 text-left text-sm font-medium text-white">Estatus</th>
                <th className="px-6 py-4 text-right text-sm font-medium text-white">Acciones</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/10">
              {ads.map((ad) => (
                <tr key={ad.id} className="hover:bg-white/5">
                  <td className="px-6 py-4">
                    {ad.image_path ? (
                      <img src={ad.image_path} alt={ad.title} className="w-16 h-16 object-cover rounded-lg" />
                    ) : (
                      <div className="w-16 h-16 bg-white/10 rounded-lg flex items-center justify-center">
                        <ImageIcon className="w-6 h-6 text-[#A2A2A2]" />
                      </div>
                    )}
                  </td>
                  <td className="px-6 py-4 text-white font-medium">{ad.title}</td>
                  <td className="px-6 py-4 text-[#A2A2A2] text-sm max-w-md truncate">{ad.description || '-'}</td>
                  <td className="px-6 py-4 text-white">{ad.display_order}</td>
                  <td className="px-6 py-4">
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                      ad.status === 'active' ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'
                    }`}>
                      {ad.status === 'active' ? 'Activo' : 'Inactivo'}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-right">
                    <div className="flex items-center justify-end gap-2">
                      <Button variant="ghost" size="sm" onClick={() => handleOpenModal(ad)} className="text-white hover:bg-white/10">
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => handleDelete(ad.id)} className="text-red-400 hover:bg-red-500/10">
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </div>
      )}

      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="bg-black border-white/10 text-white max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-2xl">{selectedAd ? 'Editar anuncio' : 'Nuevo anuncio'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="title">Título *</Label>
              <Input id="title" value={formData.title} onChange={(e) => setFormData({ ...formData, title: e.target.value })} required className="bg-white/5 border-white/10 text-white" />
            </div>
            <div>
              <Label htmlFor="image_path">URL de imagen *</Label>
              <Input id="image_path" value={formData.image_path} onChange={(e) => setFormData({ ...formData, image_path: e.target.value })} required className="bg-white/5 border-white/10 text-white" />
            </div>
            <div>
              <Label htmlFor="description">Descripción</Label>
              <textarea id="description" value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} rows={4} className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white placeholder-[#A2A2A2] focus:outline-none focus:ring-2 focus:ring-white/20" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="status">Estatus *</Label>
                <select id="status" value={formData.status} onChange={(e) => setFormData({ ...formData, status: e.target.value })} required className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-white/20">
                  <option value="active">Activo</option>
                  <option value="inactive">Inactivo</option>
                </select>
              </div>
              <div>
                <Label htmlFor="display_order">Orden</Label>
                <Input id="display_order" type="number" value={formData.display_order} onChange={(e) => setFormData({ ...formData, display_order: parseInt(e.target.value) || 0 })} className="bg-white/5 border-white/10 text-white" />
              </div>
            </div>
            <div className="flex justify-end gap-2 pt-4">
              <Button type="button" variant="outline" onClick={() => setIsModalOpen(false)}>Cancelar</Button>
              <Button type="submit" className="bg-white text-black hover:bg-white/90">{selectedAd ? 'Actualizar' : 'Crear'}</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdsManagement;

